import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Crosshair, ShieldAlert, Check, FileCheck2, Zap, Clock, ShieldCheck } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { api, money } from "@/lib/api";
import { Reveal, MaskedLines } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";

const HERO_IMG =
  "https://images.pexels.com/photos/10720375/pexels-photo-10720375.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const LASER_IMG =
  "https://images.unsplash.com/photo-1615378809683-6737a9e362f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxsYXNlciUyMGVuZ3JhdmluZyUyMG1ldGFsJTIwZGFya3xlbnwwfHx8fDE3ODQ4MjMwMzF8MA&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  { n: "01", title: "The Design", body: "We translate your idea into a laser-ready vector — text, logo, or full custom art. You approve every detail before a single line is cut." },
  { n: "02", title: "The Precision Cut", body: "Fiber lasers etch permanent, high-contrast marks into steel, aluminum, and coated surfaces — accurate to a fraction of a millimeter. No stickers. No fading." },
  { n: "03", title: "The Finish", body: "Hand-inspected, deburred, and finished. Every piece leaves the shop built to be used, carried, and kept for years." },
];

const BENEFITS = [
  { icon: FileCheck2, title: "Free digital proof", body: "Approve exactly how it looks before we cut. No surprises." },
  { icon: Zap, title: "Permanent laser mark", body: "Etched into the material — never printed, never fades." },
  { icon: Clock, title: "Fast turnaround", body: "Most orders ship within 5–7 business days." },
  { icon: ShieldCheck, title: "Made to last", body: "Precision work, guaranteed. If it's not right, we make it right." },
];

const FAQS = [
  { q: "How does the custom process work?", a: "Pick a product and use the live customizer, or send us your idea through Custom Work. We turn it into a laser-ready proof, you approve it, then we engrave and ship." },
  { q: "Do I get to see it before you engrave?", a: "Always. You receive a free digital proof and nothing is cut until you sign off on the placement, font, and artwork." },
  { q: "What's your turnaround time?", a: "Most single items ship within 5–7 business days after proof approval. Bulk and corporate runs are quoted individually." },
  { q: "What materials can you engrave?", a: "Powder-coated and stainless steel, anodized aluminum, brass, and most coated metals. Not sure about your item? Ask us." },
  { q: "Can you engrave firearm parts?", a: "Yes — non-serialized accessories only (slides, barrels, small parts). We are NOT an FFL and do not accept firearms or any serialized components. Services focus on slide engraving and barrel porting." },
  { q: "Do you offer bulk or corporate orders?", a: "Absolutely. Metal cards, branded drinkware, and gifting at volume are our specialty. Start a Custom Work request with your quantity." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ================= HERO ================= */}
      <section ref={heroRef} className="relative h-screen min-h-[700px] overflow-hidden flex items-center">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Machined metal detail" className="w-full h-full object-cover" style={{ filter: "grayscale(1) brightness(0.7) contrast(1.15)" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black" />
          <div className="absolute inset-0 bg-black/30" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 1 }}
            className="font-num uppercase tracking-[0.4em] text-primary text-xs md:text-sm mb-6 flex items-center gap-3"
          >
            <Crosshair className="w-4 h-4" /> 4th Watch Concepts — Precision · Purpose · Personal
          </motion.p>

          <h1 className="font-display text-[15vw] md:text-[10vw] leading-[0.85] tracking-tight">
            <MaskedLines lines={["Precision you", "can hold onto."]} lineClassName="italic" />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <p className="font-body text-base md:text-lg text-foreground/70 max-w-md">
              Premium engraving for tumblers, metal cards, jewelry, and non-serialized firearm accessories.
            </p>
            <div className="flex gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="btn-forge group bg-primary text-primary-foreground px-8 py-4 font-num uppercase tracking-widest text-sm flex items-center gap-2 hover:bg-white"
              >
                Start Customizing
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Link>
              <Link
                to="/quote"
                data-testid="hero-quote-btn"
                className="btn-forge border border-white/25 px-8 py-4 font-num uppercase tracking-widest text-sm hover:border-primary hover:text-primary"
              >
                Custom Idea
              </Link>
            </div>
          </motion.div>
        </motion.div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 font-num text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          Scroll to explore
        </div>
      </section>

      <EditorialMarquee />

      {/* ================= BENEFITS / TRUST BAR ================= */}
      <section data-testid="benefits-bar" className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="bg-[#0A0A0A] p-8 h-full hover:bg-[#111] transition-colors duration-500">
                  <Icon className="w-6 h-6 text-primary mb-5" strokeWidth={1.5} />
                  <h3 className="font-num uppercase tracking-widest text-sm mb-2">{b.title}</h3>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ================= MANIFESTO / PROCESS ================= */}
      <section id="process" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-20">
            <h2 className="font-display text-5xl md:text-7xl leading-none max-w-xl">
              The process behind every mark
            </h2>
            <p className="font-body text-muted-foreground max-w-sm">
              Three disciplined stages. Zero shortcuts. Every piece earns its finish.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12}>
              <div className="bg-[#0A0A0A] p-10 h-full group hover:bg-[#111] transition-colors duration-500">
                <span className="font-num text-primary text-6xl block mb-6">{c.n}</span>
                <h3 className="font-display text-3xl mb-4">{c.title}</h3>
                <p className="font-body text-muted-foreground leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= BRAND ESSENCE ================= */}
      <section className="border-y border-white/10 bg-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
          <Reveal>
            <p className="font-num uppercase tracking-[0.3em] text-primary text-xs mb-8">Brand Essence</p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-white/10 border border-white/10">
              {["Precision", "Intentionality", "Vigilance", "Craftsmanship", "Trust", "Ownership"].map((v) => (
                <div key={v} className="bg-black p-6 text-center hover:bg-[#111] transition-colors duration-500">
                  <span className="font-num uppercase tracking-[0.15em] text-sm text-foreground/80">{v}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= PRODUCT GRID ================= */}
      <section id="shop" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 md:pb-40">
        <Reveal>
          <div className="flex items-end justify-between mb-12">
            <h2 className="font-display text-5xl md:text-7xl leading-none">The Collection</h2>
            <Link to="/shop" className="link-underline font-num uppercase tracking-widest text-sm hidden md:flex items-center gap-2 hover:text-primary">
              View all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {products.slice(0, 6).map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.1}>
              <Link
                to={`/customize/${p.id}`}
                data-testid={`product-card-${p.id}`}
                className={`group block relative overflow-hidden border border-white/10 bg-[#0d0d0d] ${
                  i === 0 ? "md:col-span-2 md:row-span-1" : ""
                }`}
              >
                <div className={`overflow-hidden ${i === 0 ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-[transform,opacity] duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                  <div>
                    <span className="font-num uppercase tracking-widest text-[10px] text-primary">{p.category}</span>
                    <h3 className="font-display text-2xl md:text-3xl mt-1">{p.name}</h3>
                    <p className="font-body text-sm text-muted-foreground mt-1">from {money(p.base_price)}</p>
                  </div>
                  <div className="w-11 h-11 border border-white/20 flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-colors">
                    <ArrowUpRight className="w-5 h-5" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= FIREARM DISCLAIMER ================= */}
      <section className="border-y border-white/15 bg-[#0d0d0d]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20 flex flex-col md:flex-row gap-8 items-start">
          <ShieldAlert className="w-10 h-10 text-primary shrink-0" />
          <div>
            <h3 className="font-display text-3xl md:text-4xl mb-4">Firearm engraving — the honest terms</h3>
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3 font-body text-foreground/80 max-w-3xl">
              {[
                "We are NOT an FFL. We do not accept or transfer firearms.",
                "No serialized parts or frames — ever.",
                "Non-serialized accessories only (slides, barrels, small parts).",
                "Services limited mainly to slide engraving & barrel porting.",
              ].map((t) => (
                <div key={t} className="flex gap-3 items-start">
                  <Check className="w-4 h-4 text-primary mt-1 shrink-0" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= GALLERY ================= */}
      <section id="gallery" className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <Reveal>
          <h2 className="font-display text-5xl md:text-7xl leading-none mb-12">Recent work</h2>
        </Reveal>
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {gallery.map((g, i) => (
            <Reveal key={g.id} delay={(i % 3) * 0.08}>
              <figure className="group relative mb-5 break-inside-avoid overflow-hidden border border-white/10">
                <img
                  src={g.image}
                  alt={g.title}
                  className="w-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-[transform,opacity] duration-700"
                />
                <figcaption className="absolute inset-0 flex items-end p-5 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div>
                    <span className="font-num uppercase tracking-widest text-[10px] text-primary">{g.category}</span>
                    <p className="font-display text-2xl">{g.title}</p>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="border-t border-white/10 bg-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28">
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.1}>
                <div className="bg-black p-10 h-full">
                  <span className="font-display text-6xl text-primary leading-none">“</span>
                  <p className="font-display text-2xl italic leading-snug mt-2 mb-8">{t.quote}</p>
                  <p className="font-num uppercase tracking-widest text-sm">{t.name}</p>
                  <p className="font-body text-sm text-muted-foreground">{t.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section id="faq" className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-28 md:py-36">
          <Reveal>
            <p className="font-num uppercase tracking-[0.3em] text-primary text-sm mb-4">Answers</p>
            <h2 className="font-display text-5xl md:text-7xl leading-none mb-12">Questions, handled</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="border-t border-white/10">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`} className="border-b border-white/10">
                  <AccordionTrigger className="font-display text-xl md:text-2xl text-left hover:no-underline hover:text-primary py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-base text-muted-foreground leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="relative overflow-hidden">
        <img src={LASER_IMG} alt="Laser engraving" className="absolute inset-0 w-full h-full object-cover opacity-25" style={{ filter: "grayscale(1) contrast(1.1)" }} />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-center">
          <Reveal>
            <h2 className="font-display text-5xl md:text-8xl leading-[0.9] mb-8">
              Have something<br />in mind?
            </h2>
            <Link
              to="/quote"
              data-testid="cta-quote-btn"
              className="btn-forge group inline-flex items-center gap-3 bg-primary text-primary-foreground px-10 py-5 font-num uppercase tracking-widest text-sm hover:bg-white"
            >
              Request a custom quote
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
